// Shared source of truth for the set of forms Alpaca Intelligence OWNS in the
// audit trail, plus the per-form nav-id helpers.  Imported by BOTH the audit
// page (to scope a single-form log) and AppShell (to build the dynamic
// "Audit Trail Log AI" per-form nav items).  Kept in ONE place so the id
// encoding used to BUILD a nav item and to DECODE it back to a FormName can
// never drift.

/** FormNames that Alpaca Intelligence OWNS (its migrated forms write these audit
 *  rows via IAuditTrailService). The "Audit Trail Log AI" module shows ONLY these;
 *  Data Center → All Forms shows everything ELSE (legacy ALPACA/XAF forms still on
 *  the shared dbo.AuditData). Maintained here as the single split source — add a
 *  form here when it's migrated to AI. Match FormName values EXACTLY as written to
 *  dbo.AuditData. */
export const AI_AUDIT_FORMS: ReadonlySet<string> = new Set([
  // Reporting
  "Custom Reporting",
  "Report Validation",
  "Report Approval",
  "Report Delivery",
  "Report Tracking",
  "Report Signing",
  "Batch Reporting",
  "MOR Reporting",
  "Ad Hoc Reporting",
  "MOR Template Setup",
  "EDD Report Generator",
  "Report Delivery Settings",
  "Project Notes",
  "Case Narrative",
  // CRM
  "Unfollowed Clients",
  "Notes in Clients",
  "Notes in Prospects",
  "Scheduler",
  "Contacts",
  // Analysis / QC
  "QC Batches",
  "Sample Prep Batches",
  "Result Approval",
  "Result Validation",
  "Batch Data Review",
  "SDMS Result Validation",
  "SDMS Result Entry",
  "Field Data Review 2",
  "MDL Studies",
  // Settings ▸ Analysis ▸ PLM Reference Library (2026-09-28) — an AI-owned form,
  // so its Update/Delete rows belong in the "Audit Trail Log AI" module.
  "PLM Reference Library",
  // Sampling
  "Sample Registration",
  "Sampling Allocation",
  "Sample Transfer",
  "Field Intake",
  "Field Data Tracking",
  "Field Data Entry",
  "Task Calendar",
  "Scheduled Task Tracking",
  "Sample Container pH Check",
  // Client Request module (2026-09-09) — migrated, writes via IAuditTrailService.
  "Client Request",
  // Settings / master data
  "Current Language",
  "Time Zones",
  "Country",
  "State",
  "City",
  "Division",
  "Position",
  "Department",
  "Division Department",
  "Glossary",
  "Message Editor",
  "Email Alert Settings",
  "App Config",
  // Notification Center — In-App (bell) alert acknowledgements (InAppAlertsController, 2026-10-01).
  "Notification Center",
  // Notification Center → Pop-up Settings: the named pop-up configurations (PopupSettingsController, 2026-10-14).
  "Notification Pop-up Settings",
  // Notification Center → Notification Rules: every rule change, written server-side (NotificationRulesController, 2026-10-24).
  "Notification Rules",
  // Notification Center → Rule Categories / Recipient Groups / Email Templates: edits + deletes (2026-10-24).
  "Notification Rule Categories",
  "Notification Recipient Groups",
  "Notification Email Templates",
  // Settings → Permit Library → Permit Row Colors: the result-grid colour rules (PermitRowColorsController, 2026-10-21).
  "Permit Row Colors",
  "Form Config",
  "Analysis Department Chain",
  "Payment Terms",
  "Payment Terms Setup",
  // Security
  "Module Access Control",
  "Users",
  "Roles",
  "Navigation",
  // Maintenance
  "Version Log",
  // Reagent Preparation (2026-09-26). "Reagent Prep Log" is registered ahead of
  // use — Wave 1 ships the three lists read-only, so only "LLT Depletion"
  // writes audit rows today; the header-form CRUD lands in the next wave and
  // must reuse the same name rather than inventing one.
  "Reagent Prep Log",
  "LLT Depletion",
  // Sample Tracking → Sample Cancellation (2026-10-06)
  "Sample Cancellation",
  // Sample SubContract Tracking → Subout Registration (2026-10-12)
  "Subout Registration",
  "Subout Test Order",
  // Accounting → Quotes (2026-10-13). AI now writes this form's Update and
  // Delete rows. The name is the one legacy already writes (it audits by nav
  // caption), so old and new history read as one trail. "Quotes Tracking" is
  // not here: that screen changes nothing in AI.
  "Open Quotes",
  // Module‑CRUD lookups (moduleCrud.ts formName values)
  'Bottle Sharing','Contract Lab',
  'Asbestos Fibers','Assign Dashboard To User','CMDP Sample Category','Category','Container','EDI Sheet Mapping',
  'Fiber Type Values','Fiber Type','Holidays','MSR Data Import Config','MSR Water System','Materials',
  'Method Category','Method','Monthly Summary Repository','Non-Asbestos Fibers','Non-Fibrous Components',
  'Other Components','Permit Library','Permit Setup','Permit Types','Preservative','QC Role',
  'QC Root Role','QC Source','QC Type','Report Category','Report Type','Request Category','Result Range',
  'Sample Category','Storage','Temperature','Test Code','Turn Around Time','Unconventional Analysis','Units',
  // Settings → Utility masters migrated to AI (2026-09; add each new audited form here
  // AND to the §14b.2 FormName registry — this list is what "Audit Trail Log AI" shows).
  'Reason for Change','Client','Client Account Number','PLM Sample Notes','PLM Dispersion Oil','Daily QC Data Table',
  'Daily QC Category','Parser Setup','Parameter Match','QC Type Match','ICP-MS Mass Pick',
  'Document Category','EDD Category','Glossary Category','Action Category','Problem Category',
  'NCA Reason','Delivery Priority','Grade','Item Categories','Manufacturers','Package Units',
  'ICM Project','Shipping Options','Ship via','Vendor Evaluation Items','Deployment Team',
  'Inspection Category','Prep Options','Instrument Category','Maintenance Category','Freq Code',
  'Service Company','Skip Reason','Task Ownership','Task Checklist','Routes','Sample Sites',
  'Workflow Status Chain','Collectors','Check Points Setup','Payment Status','Job Inactivation',
  'eNotification Content Template','PWS ID','PWS Name','Sample Type','System Type','Water Type',
  'Bare','Code','Component','Kind','Rejection Criteria','Repeat Location','Sample Status',
  'Sanitarian','Sanitarian Temperature','Site Source','Size','User Defined Dropdowns',
  'Air Volume','Total Surface Area','Topic','Sub Lab Parameter Match','Sub Lab Qc Parameter Match',
  'Item Charge Pricing Category','Accreditation','Brought By','Holding Time','Matrix','Prep Types',
  'Preserve Condition','Sample Matrix','Tab Controls','UnFollow Settings','Sample Field Configuration',
  'QC Category','Study ID','Study Name','Source','Calculation Approach','Operator','Standard Name',
  'WS Prep Type','WS Storage Name','CoverPage NELAP','MSR Category','Project Category',
  'Fiber Colors','Control Chart','Control Chart Signing','Audit Trail Configuration',
  // Settings → Instrument Management (legacy "LabwareSettings", 2026-09-18)
  'Instrument','Instrument Certificate','Instrument Attachment','Maintenance Setup','Maintenance Queue',
  // Settings → SDMS
  'Sequence Setup',
  'Scientific Data Table',
  'Template Types',
  // ICM Operations (2026-09-29). These write audit rows through
  // IAuditTrailService like any other AI form, but were never listed here, so
  // the "Audit Trail Log AI" module did not offer them -- the same gap recorded
  // for Turn Around Time. The per-form toolbar chip was already wired.
  'Item Initialization',
  'Vendors COA',
  // The rest of ICM Operations, listed when each got its toolbar chip (2026-11-02).
  'Item Requisition', 'Requisition Review', 'Ordering Items', 'Receiving Items',
  'Item Distribution', 'Item Consumption', 'Fractional Consumption', 'Item Disposal',
  // Settings → Utility → Inventory Management (2026-09-22)
  'Assign Tests To Item', 'Assign Items To Test',
  // Analysis → PLM result entry (asbestos bulk; Phase 1, 2026-09-22)
  'PLM',
  // Settings → Analysis (2026-09-23)
  'Daily QC Log Book Builder',
  // Analysis → Daily QC (data entry — entry Update/Delete audited, 2026-09-28)
  'Daily QC',
  // System → SQL Query Console (saved-query store — Update/Delete audited, TaskID 29825)
  'SQL Query Console',
  // Toolbox → SDMS Template Builder (port of Alpaca Studio, 2026-09-29 — Update/Delete audited)
  'SDMS Template Builder',
  // System → Dashboard → Dashboard (DevExpress designer saves + delete, ADR-0016)
  'Dashboard',
  // (ACT writes no audit row yet — reserved; Result Entry is Analysis-team-owned.)
]);

/** Every AuditData.FormName a Sample Registration job's history is written under.
 *  The screen's child forms (Received Dates, Samples, Tests, Containers, Sample
 *  Condition Check, Notes, Attachments, Images…) are separate audited objects, each
 *  with its OWN FormName but the SAME Source (the job Oid) — so a log that asks for
 *  'Sample Registration' alone shows the header and silently drops the rest.
 *  The first eight are ALPACA's AuditData_ListView_Sampleregistration filter
 *  (AuditlogsummaryViewController); the rest are written against the job by the
 *  Sample Transfer / pH Check screens and the legacy Test View / Sample Login /
 *  Sample Condition Check sub-objects. ONE list: the Data Center "Sample
 *  Registration" log and the detail-page Audit button both read it. */
export const SAMPLE_REGISTRATION_AUDIT_FORMS: readonly string[] = [
  'Sample Registration', 'SampleBottleAllocation', 'ReceivedDate',
  'SampleConditionCheck', 'Image', 'Notes', 'Attachment', 'Test Assignment',
  'Sample Container pH Check',
  'SampleConditionCheckComment', 'SampleConditionCheckpoints',
  'Test View', 'Sample Login', 'Samplelogin',
];

/** Nav-id prefix for a single form's AI audit-log leaf (routed by AppShell's
 *  `page.startsWith('at-')` → AuditTrailPage, decoded via aiFormFromNavId). */
export const AI_FORM_NAV_PREFIX = "at-aiform-";

/** URL-safe slug for a FormName (nav ids become the URL /m/<id>, so no spaces). */
const slug = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** The per-form audit nav id for a FormName (e.g. "Custom Reporting" → "at-aiform-custom-reporting"). */
export const aiFormNavId = (name: string): string => AI_FORM_NAV_PREFIX + slug(name);

/** Reverse map slug-id → the EXACT FormName.  Built once from AI_AUDIT_FORMS
 *  (the known, static universe), so decoding is exact — no lossy un-slug. */
const _byNavId: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const name of AI_AUDIT_FORMS) {
    const id = aiFormNavId(name);
    // Collision guard: the known set slugs uniquely; if two ever collide, keep
    // the first and leave the second reachable only via the consolidated log.
    if (!m[id]) m[id] = name;
  }
  return m;
})();

/** The exact FormName for a per-form audit nav id, or undefined if not one. */
export const aiFormFromNavId = (id: string): string | undefined => _byNavId[id];
